import { useEffect, useState } from "react";
import { Link, router } from "@inertiajs/react";
import { usePage } from "@inertiajs/react";
import NavMenu from "@components/nav_menu.tsx";
import GreenhouseLogo from "@images/greenhouse_logo.svg";
import GreenhouseIconDarkGreen from "@images/greenhouse_icon_dark_green.svg";
import GreenhouseIconWhite from "@images/greenhouse_icon_white.svg";
import HamburgerIcon from "@sprout/icons/hamburger.tsx";
import CloseIcon from "@sprout/icons/close.tsx";
import "./header.scss";
import useTranslation from "@hooks/use_translation.ts";
import useDevice from "@hooks/use_device.ts";
import { Row } from "@sprout/layout.tsx";
import CandidateIcon from "@sprout/icons/candidate.tsx";
import QuestionIcon from "@sprout/icons/question.tsx";
import SupportDialog from "@components/support_dialog.tsx";
import { Tag } from "@/sprout/tag";
import axios from "axios";

type PageProps = {
  url: string;
  isJobSearchEnabled: boolean;
};

type Props = {
  hideProfile?: boolean;
  paths?: {
    dashboard: string;
    applications: string;
    jobSearch: string;
    profile: string;
    reportBugPath: string;
    signOut: string;
  };
};

const Header = ({
  hideProfile = false,
  paths = {
    dashboard: "/dashboard",
    applications: "/applications",
    jobSearch: "/jobs",
    profile: "/profile",
    reportBugPath: "/users/report_bug",
    signOut: "/users/sign_out",
  },
}: Props) => {
  const { url, isJobSearchEnabled } = usePage<PageProps>().props;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSupportDialogOpen, setIsSupportDialogOpen] = useState(false);
  const device = useDevice();

  const { t } = useTranslation("misc");

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
  }, [mobileMenuOpen]);

  const openFeedbackModal = () => {
    setMobileMenuOpen(false);
    setIsSupportDialogOpen(true);
  };

  const openAboutLink = () => {
    setMobileMenuOpen(false);
    window.open("https://my.greenhouse.com/", "_blank");
  };

  const logOut = async () => {
    setMobileMenuOpen(false);
    localStorage.removeItem("JOB_SEEKERS_REACT_QUERY_CACHE");

    try {
      await axios.delete(paths.signOut);
    } catch (e) {
      console.error(e);
    } finally {
      // reload the page to clear the session
      window.location.reload();
    }
  };

  const renderLogo = () => {
    if (device.isMobile) {
      const MobileIcon = mobileMenuOpen ? GreenhouseIconWhite : GreenhouseIconDarkGreen;
      return (
        <Link href="/" className="header__logo-wrapper">
          <img src={MobileIcon} alt="Greenhouse icon" />
        </Link>
      );
    }

    return (
      <Link href="/" className="header__logo-wrapper">
        <img src={GreenhouseLogo} alt="Greenhouse logo" />
      </Link>
    );
  };

  const renderLinks = () => (
    <>
      <Link
        href={paths.dashboard}
        onClick={() => setMobileMenuOpen(false)}
        className={`header__item ${url === paths.dashboard ? "selected" : ""}`}
      >
        {t("home")}
      </Link>
      <Link
        href={paths.applications}
        onClick={() => setMobileMenuOpen(false)}
        className={`header__item ${url === paths.applications ? "selected" : ""}`}
      >
        {t("applications")}
      </Link>
      {isJobSearchEnabled && (
        <Link
          href={paths.jobSearch}
          onClick={() => setMobileMenuOpen(false)}
          className={`header__item ${url === paths.jobSearch ? "selected" : ""}`}
        >
          <Row align="center" gap="8px">
            {t("jobSearch")}
            <Tag label={t("new")} color="#dcf2ec" />
          </Row>
        </Link>
      )}
      {!isJobSearchEnabled && (
        <Row gap="8px" align="center">
          <span
            aria-disabled={true}
            className="header__item header__item--disabled whitespace-nowrap cursor-pointer"
          >
            {t("jobSearch")}
          </span>
          <Tag label={t("comingSoon")} />
        </Row>
      )}
      {device.isMobile && mobileMenuOpen && (
        <>
          {!hideProfile && (
            <Link
              href={paths.profile}
              onClick={() => setMobileMenuOpen(false)}
              className={`header__item ${url === paths.profile ? "selected" : ""}`}
            >
              {t("profile")}
            </Link>
          )}
          <button className={"header__item"} onClick={() => openFeedbackModal()}>
            {t("submit_feedback")}
          </button>
          <a
            href="https://my.greenhouse.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="header__item"
            onClick={() => setMobileMenuOpen(false)}
          >
            {t("about")}
          </a>
          <button className="header__item" onClick={logOut}>
            {t("logOut")}
          </button>
        </>
      )}
    </>
  );

  const renderDesktopMenuButtons = () => (
    <div className="header__desktop-menu-buttons">
      {!hideProfile && (
        <NavMenu
          icon={<CandidateIcon />}
          items={[
            { value: 0, label: t("profile"), onSelect: () => router.visit(paths.profile) },
            { value: 1, label: t("logOut"), onSelect: logOut },
          ]}
        />
      )}
      <NavMenu
        icon={<QuestionIcon />}
        items={[
          { value: 0, label: t("submit_feedback"), onSelect: openFeedbackModal },
          { value: 1, label: t("about"), onSelect: openAboutLink },
        ]}
      />
    </div>
  );

  const renderMobileMenuButton = () => (
    <button
      className="header__mobile-menu-button"
      onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
    >
      {mobileMenuOpen ? (
        <CloseIcon size={"md"} color={"white"} />
      ) : (
        <HamburgerIcon size={"md"} color={"dark-green"} />
      )}
    </button>
  );

  return (
    <div className="z-1">
      <header className={`header ${mobileMenuOpen ? "header__mobile-menu-open" : ""}`}>
        <div className="header__content">
          <div className="header__left whitespace-nowrap">
            {renderLogo()}
            {device.isWeb && renderLinks()}
          </div>
          {device.isWeb && renderDesktopMenuButtons()}
          {device.isMobile && renderMobileMenuButton()}
        </div>
        {device.isMobile && mobileMenuOpen && (
          <div className="header__mobile-menu">{renderLinks()}</div>
        )}
        <SupportDialog
          isOpen={isSupportDialogOpen}
          setIsDialogOpen={setIsSupportDialogOpen}
          bugReportPath={paths.reportBugPath}
        />
      </header>
    </div>
  );
};

export default Header;
